import {getStationCode} from "../network/NetworkUtil";
import {
    AR,
    CORE_AMZL,
    DATE_TIME_FORMAT,
    DEFAULT_CAPABILITIES,
    DEFAULT_REFRESH_INTERVAL,
    EMPTY_STRING,
    ENVIRONMENT,
    IS_ROUTE_ADJUSTMENT_ENABLED,
    IS_RSWS_BOSON_FLOW_ENABLED,
    REGION, ROUTE_VIEW,
    SSD,
    STAGE
} from "./Constants";
import {ASSIGNABLE, IN_PROGRESS} from "./EntityStatusManager";
import {getFeaturesFromParent} from "../handler/ParentDataHandler";

export function convertSecondsToTimeFormat(seconds) {
    const hours = Math.floor(seconds / 3600) % 24;
    const minutes = Math.floor(seconds / 60) % 60;
    const secs = Math.round(seconds % 60);
    return ([hours, minutes, secs]
        .map(v => v < 10 ? "0" + v : v)
        .filter((v, i) => v !== "00" || i > 0)
        .join(":"));
}

export function convertEpochToHHMMSS(epochTime) {
    if(!epochTime){
        return "-";
    }
    const time = new Date(parseInt(epochTime, 10));
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const secs = time.getSeconds();
    return ([hours, minutes, secs]
        .map(v => v < 10 ? "0" + v : v)
        .join(":"));
}

export function getTimeDiffFromCurrentTimeInHHMMSSFormat(time) {
    if (!time) {
        return "...";
    }
    const curDate = new Date();
    const timeDiff = curDate.getTime() - time;
    return convertSecondsToTimeFormat(timeDiff / 1000);
}

export function convertMillisecondsToHHMM(milliseconds) {
    if(!milliseconds){
        return "-";
    }
    const time = new Date(parseInt(milliseconds, 10));
    return ('0' + time.getHours()).slice(-2) + ':' + ('0' + time.getMinutes()).slice(-2);
}

export function getDateInMMDDYYYYWithOffset(offset){
    let presentDate = new Date();
    presentDate.setDate(presentDate.getDate()+offset)
    return (presentDate.getMonth() + 1) + "/" + presentDate.getDate() + "/" + presentDate.getFullYear();
}

export function getBaseUrl(endpoint) {
    const url = new URL(endpoint);
    return url.origin
}

export function naIfDefault(value) {
    return !value || value === "..." ? "NA" : value;
}

/**
 * This method return the refresh interval in milliseconds
 * @returns {number}
 */
export function getRefreshInterval(stationConfig) {
    if (stationConfig) {
        return stationConfig.refreshInterval;
    }
    return DEFAULT_REFRESH_INTERVAL;
}

export function parseCapabilitiesResponse(response) {
    return response ? JSON.parse(response.capability) : '';
}

export function formatAlias(alias) {
    return (alias != null && alias.indexOf('@') > -1 ? alias.split('@')[0] : alias);
}

/**
 * Compare 2 arrays of string and returns true if they are equal.
 * @param array1
 * @param array2
 * @return {boolean}
 */
export function isArrayEquals(array1, array2) {
    if (!array1 || !array2 || array1.length !== array2.length) {
        return false;
    }
    const sortedArray1 = array1.sort();
    const sortedArray2 = array2.sort();
    for(let i = sortedArray1.length; i--;) {
        if(sortedArray1[i] !== sortedArray2[i])
            return false;
    }
    return true;
}

/**
 * Calculate the OTS metric for list of routes.
 * @param routes
 */
export function calculateOTSMetric(routes) {
    let filteredRoutes = routes.filter(route => route.stageTime && route.plannedDispatchTime);
    if(!filteredRoutes.length){
        return "-";
    }
    let otsRoutes = 0;
    filteredRoutes.forEach(route => {
        if(route.plannedDispatchTime - route.stageTime >= 0){
            otsRoutes++
        }
    })
    return ((otsRoutes/filteredRoutes.length) * 100).toFixed(2);
}

/**
 * Return cluster from route code.
 * Supports routing 3.0 regex.
 *
 * @param routeCode
 * @return {*}
 */
export function getCluster(routeCode) {
    if (!routeCode) {
        return "";
    }
    return routeCode.split("-")[0].replace(/[0-9]/g, '');
}

/**
 * Handles exception while reading from localStorage
 * @param key
 */
export function getFromLocalStorage(key) {
    try {
        return localStorage.getItem(key);
    } catch (e) {
        return null;
    }
}

/**
 * Handles exception while writing into localStorage
 * @param key
 * @param value
 */
export function saveIntoLocalStorage(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch (e) {
    }
}

/**
 * Saves filter state into local storage along with current station code.
 * It also translates the newState into JSON.
 * @param newState
 */
export function saveFilterState(filterKey, newState) {
    try {
        newState.stageTabViewType = ROUTE_VIEW
        const record = JSON.stringify({data: newState, stationCode: getStationCode()});
        saveIntoLocalStorage(filterKey, record);
    } catch (e) {
    }
}

/**
 * Reads filter state from local storage by translating it into Json.
 * If current station code is different the persisted one, it returns null.
 * @param filterKey
 */
export function readFilterState(filterKey) {
    try {
        const json = getFromLocalStorage(filterKey) || null;
        if (json) {
            const record = JSON.parse(json);
            if (record.stationCode === getStationCode()) {
                return record.data;
            }
        }
    } catch (ignored) {
    }
    return null;
}

/**
 * Returns the picker for this route (for AMZL sites)
 * if route is not picked yet we use RPS information
 * if route is picked we use SPS information
 *
 * @param rswsRoute
 * @return {*|string}
 */
export function getLatestPicker(capability, rswsRoute) {
    if (DEFAULT_CAPABILITIES.includes(capability)) {
        if (rswsRoute.activeAssignee) {
            return rswsRoute.activeAssignee;
        } else if (rswsRoute.routeStatus === ASSIGNABLE || (capability === CORE_AMZL && rswsRoute.routeStatus === IN_PROGRESS)) {
            return '...';
        } else {
            return Array.isArray(rswsRoute.pickAssociates) && rswsRoute.pickAssociates.length ?
                rswsRoute.pickAssociates[0].alias : '...';
        }
    }
    return "...";
}

export const isNotEmpty = (value) => {
    return value && EMPTY_STRING !== value
}

export function isARorSSD(capability) {
    return (capability && (capability === AR || capability === SSD));
}

export function isSSD(capability) {
    return (capability && capability === SSD);
}

export function isRouteChevronEnabled(stationConfig) {
    return stationConfig.highSPREnabled || stationConfig.parallelPickEnabled;
}
/**
 * Checks existence of one-box string in the url
 * @return {boolean}
 */
export function isOneBox() {
    let location = window.location.href
    return location && location.includes("onebox");
}

/**
 * Trims String to max length for displaying inside tables.
 */
export function trimColValue(value, maxLength){
    if(value && (typeof value === 'string' || value instanceof String) && value.length > maxLength){
        return value.substr(0, maxLength) + "...";
    }
    return value;
}


export function getTimeInSecond(time) {
    if (!time) {
        return 0;
    }
    const splitTime = time.split(":");
    if (splitTime.length > 2) {
        return (parseInt(splitTime[0], 10) * 60 * 60) + (parseInt(splitTime[1], 10) * 60)
            + parseInt(splitTime[2], 10);
    } else {
        return (parseInt(splitTime[0], 10) * 60) + parseInt(splitTime[1], 10);
    }
}

function convertMillisToSecond(time) {
    return Math.ceil(time/1000)
}

export function convertEpochMillisToLocalTime(epochTime) {
    const options = {hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: false};
    const date = new Date(epochTime);
    return date.toLocaleString('en-GB', options);
}

export function convertEpochMillisToLocalDateTime(epochTime) {
    const options = {month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: false};
    const date = new Date(epochTime);
    return date.toLocaleString('en-GB', options);
}

/**
 * This method is only applicable for AR/SSD
 */
export function calculateStageDuration(route) {
    if (route.pickAssociates.length > 0 && (!route.pickingErrorMessage)) {
        if (route.pickAssociates[0].pickingDuration) {
            return convertSecondsToTimeFormat(convertMillisToSecond(route.pickAssociates[0].pickingDuration))
        } else if (route.pickAssociates[0].startTime) {
            return getTimeDiffFromCurrentTimeInHHMMSSFormat(route.pickAssociates[0].startTime)
        }
    }
    return "...";
}

/**
 * Returns the current time in format [ MonthName dd, YYYY, HH:MM:SS GMT+__:__ ]
 * This pattern may slightly change according to current locale.
 * @param currentDate
 * @param locale
 * @return string
 */
export const getFormattedTime = (currentDate, locale) => {
    return new Intl.DateTimeFormat(locale, DATE_TIME_FORMAT).format(currentDate)
}

export function isDevelopmentEnv(){
    return process.env.NODE_ENV !== ENVIRONMENT.PROD && process.env.NODE_ENV !== ENVIRONMENT.TEST;
}

/**
 * Parses domain name and NODE_ENV variable to get current stage.
 * @return {string}
 */
export function getStage() {
    if (isDevelopmentEnv()) {
        return STAGE.DEV;
    }
    const hostname = window.location.hostname;
    if (hostname.includes("integ.")) return STAGE.BETA;
    else if (hostname.includes("master.")) return STAGE.GAMMA;
    else return STAGE.PROD
}

/**
 * Parses domain name and NODE_ENV variable to get current region.
 * @return {string}
 */
export function getRegion() {

    if (isDevelopmentEnv()) {
        return REGION.JP;
    }
    const hostname = window.location.hostname;
    if (hostname.includes("na.")||hostname.includes("us.")) return REGION.NA;
    else if (hostname.includes("eu.") || hostname.includes("gb.")) return REGION.EU;
    else if (hostname.includes("jp.")) return REGION.JP;
    else if(hostname.includes("in.")) return REGION.IN;
    else return REGION.JP;
}

/**
 * Checks if Region is EU/NA/JP or capability is SSD
 * @param capability
 * @returns {boolean}
 */
export function isNotINRegionOrSSD(capability) {
    return (getRegion() !== REGION.IN || isSSD(capability));
}

/**
 * Checks if Region is IN and capability is non SSD.
 * @param capability
 * @returns {boolean}
 */
export function isNotSSDAndINRegion(capability) {
    return (getRegion() === REGION.IN && !isSSD(capability));
}

/**
 * Input is epoch, output is date-time in browser local time zone
 * For eg:
 *  Input: 1637145000000
 *  Output: 2021/11/17 16:00:00 (Sweden date time format)
 * Ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleString
 */
export function convertEpochToLocaleFormat(epoch) {
    if (epoch && typeof epoch === 'number') {
        return new Date(epoch).toLocaleString('sv')
    }
    return "";
}

/**
 * Checks if the RSWS Boson Flow feature is enabled.
 *
 * @returns {Promise<boolean>} A Promise that resolves to a boolean indicating whether the feature is enabled.
 */
export async function isRSWSBosonFlowEnabled() {
    try {
        const response = await getFeaturesFromParent([IS_RSWS_BOSON_FLOW_ENABLED]);
        return response.data && response.data.isRSWSBosonFlowEnabled;
    } catch (ex) {
        console.error("Error occurred while fetching config from Boson App config");
    }
    // Return false if there's an error or if the feature is not enabled.
    return false;
}

/**
 * Checks if the Route Adjustment Flow feature is enabled.
 *
 * @returns {Promise<boolean>} A Promise that resolves to a boolean indicating whether the feature is enabled.
 */
export async function isRouteAdjustmentEnabled() {
    try {
        const response = await getFeaturesFromParent([IS_ROUTE_ADJUSTMENT_ENABLED]);
        return response.data && response.data.isRouteAdjustmentEnabled;
    } catch (ex) {
        console.error("Error occurred while fetching config from Boson App config");
    }
    return false;
}



// WEBPACK FOOTER //
// ./src/components/util/Util.js